import {
  Shield,
  RefreshCw,
  AlertTriangle,
  Ban,
  Activity,
  Globe,
  Unlock,
  XCircle,
  Loader2,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { useBehavioralSecurityPanel } from './useBehavioralSecurityPanel'
import {
  eventRouteLabel,
  eventTypeLabel,
  formatSecurityDate,
  scoreClass,
  severityClass,
} from './behavioralSecurityHelpers'

function SummaryCard({
  label,
  value,
  hint,
  icon: Icon,
  accent = 'text-[var(--brand-primary)]',
}: {
  label: string
  value: string | number
  hint?: string
  icon: typeof Shield
  accent?: string
}) {
  return (
    <div className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4 space-y-2'>
      <div className='flex items-center gap-2 text-[var(--text-muted)]'>
        <Icon size={16} className={accent} />
        <span className='text-xs font-semibold uppercase tracking-wide'>{label}</span>
      </div>
      <div className='text-2xl font-bold text-[var(--text-primary)]'>{value}</div>
      {hint && <p className='text-[10px] text-[var(--text-muted)]'>{hint}</p>}
    </div>
  )
}

export function BehavioralSecurityTab() {
  const panel = useBehavioralSecurityPanel()

  if (panel.loading && !panel.summary) {
    return (
      <div className='glass-card flex h-48 items-center justify-center'>
        <Loader2 className='animate-spin text-[var(--brand-primary)]' size={24} />
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {panel.error && (
        <div className='flex items-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 p-3 text-xs text-rose-400'>
          <XCircle size={14} /> {panel.error}
        </div>
      )}

      <div className='glass-card flex items-center justify-between p-4'>
        <div className='flex items-center gap-2'>
          <Shield size={16} className='text-[var(--brand-primary)]' />
          <span className='text-sm font-semibold text-[var(--text-primary)]'>
            Segurança Comportamental
          </span>
        </div>
        <button
          type='button'
          onClick={() => panel.fetchData(true)}
          className='rounded-lg p-2 text-[var(--text-muted)] transition-colors hover:bg-white/[0.06]'
          title='Atualizar'
        >
          <RefreshCw size={16} className={panel.refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
        <SummaryCard label='Eventos (24h)' value={panel.summary?.eventsLast24h ?? 0} hint='Inspeções e tentativas auditadas' icon={Activity} />
        <SummaryCard label='Riscos ativos' value={panel.summary?.activeRiskStates ?? 0} hint='Escopos com score > 0' icon={AlertTriangle} accent='text-amber-400' />
        <SummaryCard label='Bloqueios ativos' value={panel.summary?.blockedStates ?? 0} hint='IPs bloqueados agora' icon={Ban} accent='text-rose-400' />
        <SummaryCard label='Janela' value={`${panel.summary?.windowHours ?? 24}h`} hint='Agregação do resumo' icon={Globe} />
      </div>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <div className='glass-card overflow-hidden p-0'>
          <div className='border-b border-[var(--glass-border)] px-4 py-3'>
            <h3 className='text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]'>Top eventos (24h)</h3>
          </div>
          <div className='divide-y divide-[var(--glass-border)]'>
            {(panel.summary?.eventsByType ?? []).length === 0 ? (
              <p className='p-6 text-center text-xs text-[var(--text-muted)]'>Nenhum evento nas últimas 24h</p>
            ) : (
              panel.summary?.eventsByType.map((row) => (
                <div key={row.eventType} className='flex items-center justify-between px-4 py-3'>
                  <span className='font-mono text-xs text-[var(--text-primary)]'>{row.eventType}</span>
                  <span className='text-xs font-bold text-[var(--brand-primary)]'>{row.count}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className='glass-card overflow-hidden p-0'>
          <div className='border-b border-[var(--glass-border)] px-4 py-3'>
            <h3 className='text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]'>Top IPs (24h)</h3>
          </div>
          <div className='divide-y divide-[var(--glass-border)]'>
            {(panel.summary?.topIps ?? []).length === 0 ? (
              <p className='p-6 text-center text-xs text-[var(--text-muted)]'>Sem IPs registrados</p>
            ) : (
              panel.summary?.topIps.map((row) => (
                <div key={row.ipAddress ?? 'unknown'} className='flex items-center justify-between px-4 py-3'>
                  <span className='font-mono text-xs text-[var(--text-primary)]'>{row.ipAddress || '—'}</span>
                  <span className='text-xs font-bold text-[var(--brand-primary)]'>{row.count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <RiskStatesTable
        states={panel.riskStates}
        unblockingKey={panel.unblockingKey}
        onUnblock={panel.handleUnblock}
      />

      <EventsTable events={panel.events} limit={panel.eventsLimit} />

      <div className='glass-card flex items-center gap-4 border border-[var(--brand-primary)]/10 bg-[var(--brand-primary)]/5 p-4'>
        <div className='rounded-lg bg-[var(--brand-primary)]/10 p-2'>
          <Shield size={20} className='text-[var(--brand-primary)]' />
        </div>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wide text-[var(--text-primary)]'>
            Resposta progressiva ativa
          </p>
          <p className='mt-0.5 text-[10px] leading-relaxed text-[var(--text-muted)]'>
            Monitora todos os usuários autenticados do CRM (F12, Ctrl+Shift+I, Print Screen, menu de contexto).
            Registra quem fez, o que fez e em qual rota — sem bloquear o uso interno.
            No portal PJ (/portal) a resposta é reforçada: cópia bloqueada e sessão encerrada ao abrir DevTools.
            Contas PJ acumulam score de risco; demais perfis apenas auditam.
          </p>
        </div>
      </div>
    </div>
  )
}

function RiskStatesTable({
  states,
  unblockingKey,
  onUnblock,
}: {
  states: ReturnType<typeof useBehavioralSecurityPanel>['riskStates']
  unblockingKey: string | null
  onUnblock: (scopeType: string, scopeKey: string) => void
}) {
  return (
    <div className='glass-card overflow-hidden p-0'>
      <div className='flex items-center justify-between border-b border-[var(--glass-border)] px-4 py-3'>
        <h3 className='text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]'>Estados de risco</h3>
        <span className='text-[10px] text-[var(--text-muted)]'>{states.length} registros</span>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full border-collapse text-left'>
          <thead>
            <tr className='border-b border-[var(--glass-border)] bg-white/[0.02]'>
              {['Escopo', 'Chave', 'Score', 'Bloqueado até', 'Ação'].map((h) => (
                <th key={h} className='px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]'>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-[var(--glass-border)]'>
            {states.length === 0 ? (
              <tr>
                <td colSpan={5} className='p-8 text-center text-xs text-[var(--text-muted)]'>
                  Nenhum estado de risco ativo
                </td>
              </tr>
            ) : (
              states.map((state) => {
                const isBlocked = state.blockedUntil && new Date(state.blockedUntil) > new Date()
                const unblockKey = `${state.scopeType}:${state.scopeKey}`
                return (
                  <tr key={state.id} className='hover:bg-white/[0.02]'>
                    <td className='px-4 py-3 text-xs font-medium text-[var(--text-primary)]'>{state.scopeType}</td>
                    <td className='max-w-[200px] truncate px-4 py-3 font-mono text-[10px] text-[var(--text-muted)]' title={state.scopeKey}>{state.scopeKey}</td>
                    <td className='px-4 py-3'>
                      <span className={cn('rounded px-2 py-0.5 text-xs font-bold', scoreClass(state.score))}>{state.score}</span>
                    </td>
                    <td className='whitespace-nowrap px-4 py-3 text-[10px] text-[var(--text-muted)]'>
                      {isBlocked ? formatSecurityDate(state.blockedUntil!) : '—'}
                    </td>
                    <td className='px-4 py-3'>
                      <button
                        type='button'
                        disabled={unblockingKey === unblockKey}
                        onClick={() => onUnblock(state.scopeType, state.scopeKey)}
                        className='flex items-center gap-1.5 rounded-lg border border-[var(--brand-primary)]/30 px-2.5 py-1 text-[10px] font-semibold text-[var(--brand-primary)] transition-all hover:bg-[var(--brand-primary)]/10 disabled:opacity-40'
                      >
                        <Unlock size={12} />
                        {unblockingKey === unblockKey ? '...' : 'Desbloquear'}
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EventsTable({
  events,
  limit,
}: {
  events: ReturnType<typeof useBehavioralSecurityPanel>['events']
  limit: number
}) {
  return (
    <div className='glass-card overflow-hidden p-0'>
      <div className='flex items-center justify-between border-b border-[var(--glass-border)] px-4 py-3'>
        <h3 className='text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]'>Eventos recentes</h3>
        <span className='text-[10px] text-[var(--text-muted)]'>Últimos {limit}</span>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full border-collapse text-left'>
          <thead>
            <tr className='border-b border-[var(--glass-border)] bg-white/[0.02]'>
              {['Data', 'Usuário', 'Tipo', 'Severidade', 'IP', 'Rota / detalhe', 'Score'].map((h) => (
                <th key={h} className='px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]'>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-[var(--glass-border)]'>
            {events.length === 0 ? (
              <tr>
                <td colSpan={7} className='p-8 text-center text-xs text-[var(--text-muted)]'>
                  Nenhum evento registrado
                </td>
              </tr>
            ) : (
              events.map((event) => (
                <tr key={event.id} className='hover:bg-white/[0.02]'>
                  <td className='whitespace-nowrap px-4 py-3 font-mono text-[10px] text-[var(--text-muted)]'>
                    {formatSecurityDate(event.createdAt)}
                  </td>
                  <td className='max-w-[180px] px-4 py-3 text-[10px] text-[var(--text-primary)]'>
                    {event.user ? (
                      <div className='min-w-0'>
                        <p className='truncate font-semibold' title={event.user.name}>{event.user.name}</p>
                        <p className='truncate text-[var(--text-muted)]' title={event.user.email}>{event.user.email}</p>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className='px-4 py-3'>
                    <span className='rounded bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-[var(--text-primary)]'>
                      {eventTypeLabel(event)}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className={cn('rounded border px-2 py-0.5 text-[10px] font-semibold', severityClass(event.severity))}>
                      {event.severity}
                    </span>
                  </td>
                  <td className='px-4 py-3 font-mono text-[10px] text-[var(--text-muted)]'>{event.ipAddress}</td>
                  <td className='max-w-[260px] truncate px-4 py-3 font-mono text-[10px] text-[var(--text-primary)]' title={eventRouteLabel(event)}>
                    {eventRouteLabel(event)}
                  </td>
                  <td className='px-4 py-3 text-xs font-semibold text-[var(--text-primary)]'>
                    {event.riskScoreBefore ?? '—'} → {event.riskScoreAfter ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
